import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { agentNotificationsAPI } from '@/services/api';
import { Link } from 'react-router-dom';

type Notification = {
    id: number;
    type: string;
    title: string;
    message: string;
    entity_type?: string | null;
    entity_id?: number | null;
    read_at?: string | null;
    created_at: string;
};

const fa = new Intl.NumberFormat('fa-IR');

interface Props {
    totalUnread?: number;
}

export const NotificationsBell: React.FC<Props> = ({ totalUnread = 0 }) => {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);

    const unreadCount = items.filter(n => !n.read_at).length;

    const load = async () => {
        try {
            setLoading(true);
            const res = await agentNotificationsAPI.list({
                only_unread: false,
                limit: 10,
            });
            if (res?.success) {
                setItems(res.items || []);
            }
        } catch (e) {
            console.error('Error loading notifications:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, []);

    const markAllRead = async () => {
        try {
            const unreadIds = items.filter(n => !n.read_at).map(n => n.id);
            if (unreadIds.length === 0) return;
            await agentNotificationsAPI.markAsRead(unreadIds);
            setItems(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
        } catch (e) {
            console.error(e);
        }
    };

    const getLink = (n: Notification) => {
        if (n.entity_type === 'invoice' && n.entity_id) return `/invoices`;
        if (n.entity_type === 'order' && n.entity_id) return `/orders`;
        if (n.entity_type === 'wallet') return `/wallet`;
        if (n.entity_type === 'license') return `/licenses`;
        return '#';
    };

    return (
        <div className="relative">
            <Button
                variant="outline"
                size="icon"
                className="relative border-green-200"
                onClick={() => setOpen(o => !o)}
            >
                <Bell className="h-5 w-5 text-green-700" />
                {totalUnread > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full px-1 min-w-[16px] flex items-center justify-center">
                        {totalUnread > 9 ? '9+' : totalUnread}
                    </span>
                )}
            </Button>

            {open && (
                <Card className="absolute left-0 mt-2 w-80 z-[999999] shadow-xl border-green-100 bg-white">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-green-100">
                        <span className="text-sm font-semibold text-gray-800">اعلان‌ها</span>
                        <button
                            className="text-xs text-green-600 hover:text-green-800"
                            onClick={markAllRead}
                        >
                            علامت زدن همه
                        </button>
                    </div>

                    <div className="max-h-80 overflow-y-auto">
                        {loading ? (
                            <div className="py-6 text-center text-gray-500 text-sm">در حال بارگذاری...</div>
                        ) : items.length === 0 ? (
                            <div className="py-6 text-center text-gray-500 text-sm">اعلان جدیدی ندارید.</div>
                        ) : (
                            items.map(n => (
                                <Link
                                    key={n.id}
                                    to={getLink(n)}
                                    onClick={() => setOpen(false)}
                                    className="block border-b last:border-0 border-green-50 hover:bg-green-50/80 transition"
                                >
                                    <div className="px-3 py-2.5">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className={`text-xs font-semibold ${!n.read_at ? 'text-green-800' : 'text-gray-700'}`}>
                                                {n.title}
                                            </span>
                                            <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                                {new Date(n.created_at).toLocaleString('fa-IR')}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-[11px] text-gray-600 line-clamp-2">
                                            {n.message}
                                        </div>
                                        {!n.read_at && (
                                            <div className="mt-1 inline-flex items-center text-[10px] text-green-700 bg-green-100/70 px-2 py-0.5 rounded-full">
                                                جدید
                                            </div>
                                        )}
                                    </div>
                                </Link>
                            ))
                        )}
                    </div>

                    <div className="px-3 py-2 border-t border-green-100 text-center">
                        <Link to="/notifications">
                            <span className="text-xs text-green-700 hover:text-green-900">مشاهده همه اعلان‌ها</span>
                        </Link>
                    </div>
                </Card>
            )}
        </div>
    );
};
